/**
 * PII / 금융 데이터 마스킹 유틸.
 * 프론트엔드 1차 방어. 서버(`grip-server/src/common/utils/pii-mask.util.ts`)에서
 * 동일 규칙으로 2차 마스킹이 한 번 더 적용된다 (심층 방어). 두 구현을 반드시 동기화할 것.
 */

const EMAIL_PATTERN = /[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
const JWT_PATTERN = /ey[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
const CARD_PATTERN = /\b(?:\d[ -]?){13,19}\b/g
const EVM_ADDRESS_PATTERN = /0x[a-fA-F0-9]{40}/g
const BTC_ADDRESS_PATTERN = /\bbc1[a-zA-HJ-NP-Z0-9]{25,}\b/g

const REDACTED_KEYS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'password',
  'token',
  'accesstoken',
  'refreshtoken',
])

const QUERY_WHITELIST = new Set(['page', 'tab', 'status', 'type'])

export function maskString(value: string | undefined | null): string {
  if (!value) return ''
  return value
    .replace(EMAIL_PATTERN, '<email>')
    .replace(JWT_PATTERN, '<jwt>')
    .replace(CARD_PATTERN, '<card>')
    .replace(EVM_ADDRESS_PATTERN, '<wallet>')
    .replace(BTC_ADDRESS_PATTERN, '<wallet>')
}

export function maskDeep<T>(input: T, depth = 0): T {
  if (depth > 10) return input
  if (input === null || input === undefined) return input
  if (typeof input === 'string') return maskString(input) as unknown as T
  if (typeof input !== 'object') return input
  if (Array.isArray(input)) {
    return input.map((item) => maskDeep(item, depth + 1)) as unknown as T
  }

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (REDACTED_KEYS.has(key.toLowerCase())) {
      result[key] = '<redacted>'
      continue
    }
    result[key] = maskDeep(value, depth + 1)
  }
  return result as unknown as T
}

/**
 * URL에서 위험한 쿼리스트링 값을 제거하고 화이트리스트만 유지한다.
 * `?token=abc&page=3` → `?page=3&...masked` 형태.
 */
export function sanitizeUrl(rawUrl: string): string {
  try {
    const url = new URL(
      rawUrl,
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost'
    )
    const params: string[] = []
    let hadMasked = false
    url.searchParams.forEach((value, key) => {
      if (QUERY_WHITELIST.has(key)) {
        params.push(`${key}=${encodeURIComponent(maskString(value))}`)
      } else {
        hadMasked = true
      }
    })
    if (hadMasked) params.push('...masked')
    const query = params.length > 0 ? `?${params.join('&')}` : ''
    return `${url.pathname}${query}`
  } catch {
    return maskString(rawUrl)
  }
}

/**
 * 짧고 (<=24자) 고유한 에러 ID 생성. uuid v4의 마지막 12자 + epoch의 마지막 4자.
 * 별도 uuid 라이브러리 의존 없이 crypto 기반으로 생성.
 */
export function generateErrorId(): string {
  let uuidTail = ''
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    uuidTail = (crypto as Crypto).randomUUID().replace(/-/g, '').slice(-12)
  } else {
    // fallback: Math.random 기반 — 중복 위험 낮은 수준에서 충분
    uuidTail = Math.random().toString(36).slice(2, 14).padEnd(12, '0')
  }
  const tsTail = (Date.now() % 10000).toString().padStart(4, '0')
  return `${uuidTail}-${tsTail}`
}
